import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import
    {
        Avatar,
        Box,
        Button,
        CardContent,
        Chip,
        IconButton,
        LinearProgress,
        Stack,
        Typography,
        useTheme,
    } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { BaseWidget } from '@src/App/Components/Widgets/BaseWidget';
import { getPrimaryRoleTitle } from '@src/App/Helper/roleHelper';
import type { AppState } from '@store/store';
import { Ellipsis } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export default function TeamOverviewWidget()
{
    const theme = useTheme();
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang);
    const [expandedItems, setExpandedItems] = React.useState<Set<number>>(new Set());
    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);

    const toggleExpanded = React.useCallback((memberId: number) =>
    {
        setExpandedItems((prev) =>
        {
            const newSet = new Set(prev);
            if (newSet.has(memberId))
            {
                newSet.delete(memberId);
            } else
            {
                newSet.add(memberId);
            }
            return newSet;
        });
    }, []);

    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);
    const allFullEmployees = useSelector((state: AppState) => state.employees);
    const orgUnits = useSelector((state: AppState) => state.OUs);
    const currentUser = useSelector((state: AppState) => state.employee);

    // Get employees where current user is leader of their orgUnit
    // TODO: REMOVE FRONTEND FALLBACK - This is temporary for testing purposes
    // TODO: Backend should set current user as leader of OrgUnit 31 and add Talentillion GmbH (ID: 32) to OrgUnit 31
    // TODO: Remove the fallback condition '|| ou.id === 31' once backend is properly configured
    const myOrgUnits = orgUnits.filter((ou) => ou.leader === currentUser.id);
    const myEmployeeIds = myOrgUnits.flatMap((ou) => ou.employees.map((emp) => emp));

    const teamMembers = allEmployees
        .filter((emp) => myEmployeeIds.includes(emp.id))
        .filter((emp) => emp.id !== currentUser.id) // Exclude current user from team members
        .map((emp) =>
        {
            const nameParts = (emp.title ?? '').trim().split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

            return {
                id: emp.id,
                name: emp.title,
                role: (() =>
                {
                    const fullEmployee = allFullEmployees.find((fullEmp) => fullEmp.id === emp.id);
                    return fullEmployee
                        ? getPrimaryRoleTitle(fullEmployee.roles || []) || 'Rolle nicht definiert'
                        : 'Rolle nicht definiert';
                })(),
                status: 'Active',
                avatar: `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U',
                availability: emp.availability?.availability || Math.floor(Math.random() * 40) + 20, // Use real availability if available, otherwise mock data
                topSkills: ['React', 'TypeScript', 'CSS'], // TODO: Get from employee skills
                email: emp.email,
                pictureId: emp.pictureId,
            };
        });

    const activeMembers = teamMembers.length;
    const avgAvailability =
        teamMembers.length > 0
            ? Math.round(teamMembers.reduce((sum, member) => sum + member.availability, 0) / teamMembers.length)
            : 0;

    const getAvailabilityColor = (availability: number) =>
    {
        return availability >= 60
            ? theme.palette.success.main
            : availability >= 30
                ? theme.palette.warning.main
                : theme.palette.error.main;
    };

    const getAvailabilityGradient = (availability: number) =>
    {
        if (availability >= 60)
        {
            return `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.secondary.main} 100%)`;
        } else if (availability >= 30)
        {
            return `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.secondary.main} 100%)`;
        } else
        {
            return `linear-gradient(90deg, ${theme.palette.error.main} 0%, ${theme.palette.secondary.main} 100%)`;
        }
    };

    const handleViewTeam = () =>
    {
        navigate(`/${ELinks.MY_TEAM}`);
    };

    console.log('Team Members:', teamMembers);
    console.log('Active Members:', activeMembers);
    console.log('myOrgUnits:', myOrgUnits);
    console.log('myEmployeeIds:', myEmployeeIds);
    console.log('allEmployees:', allEmployees);
    console.log('orgUnits:', orgUnits);

    return (
        <BaseWidget>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: { xs: 50, sm: 60, md: 65 },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: theme.palette.background.paper,
                }}
            >
                <GroupIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>
                    {langStrings.employees.teamManagement}
                </Typography>
                <Chip label={activeMembers} size="small" color="primary" />
            </Box>

            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box>
                    {/* Stats */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 2,
                            p: 1.5,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}80 0%, ${theme.palette.secondary.main}80 100%)`,
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" color="primary.contrastText" sx={{ fontWeight: 600 }}>
                                {activeMembers}
                            </Typography>
                            <Typography variant="caption" color="primary.contrastText" sx={{ opacity: 0.8 }}>
                                {langStrings.employees.activeMembers}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" color="primary.contrastText" sx={{ fontWeight: 600 }}>
                                {avgAvailability}%
                            </Typography>
                            <Typography variant="caption" color="primary.contrastText" sx={{ opacity: 0.8 }}>
                                {langStrings.employees.avgAvailability}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Team Members Header */}
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {langStrings.employees.teamMembers}
                        </Typography>
                    </Stack>

                    {/* Team Member Cards */}
                    {teamMembers.map((member, index) =>
                    {
                        const isExpanded = expandedItems.has(member.id);
                        return (
                            <Box
                                key={member.id}
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: 'background.paper',
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                    },
                                }}
                            >
                                {/* Member Header */}
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: isExpanded ? 2 : 0 }}>
                                    <Avatar
                                        sx={{
                                            bgcolor: 'primary.main',
                                            width: isExpanded ? 48 : 40,
                                            height: isExpanded ? 48 : 40,
                                            fontSize: isExpanded ? '1.2rem' : '1rem',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {member.avatar}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant={isExpanded ? 'h6' : 'body1'} sx={{ fontWeight: 600 }}>
                                                {member.name}
                                            </Typography>
                                            <Chip
                                                label={langStrings.employees.active}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                            />
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            {member.role}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        sx={{
                                            color: 'text.secondary',
                                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease',
                                        }}
                                        onClick={() => toggleExpanded(member.id)}
                                    >
                                        <ExpandMoreIcon />
                                    </IconButton>
                                </Stack>

                                {/* Expanded Content */}
                                {isExpanded && (
                                    <>
                                        {/* Availability */}
                                        <Box sx={{ mb: 2 }}>
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                sx={{ mb: 1 }}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    {langStrings.employees.availability}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: getAvailabilityColor(member.availability),
                                                    }}
                                                >
                                                    {member.availability}%
                                                </Typography>
                                            </Stack>
                                            <Box
                                                sx={{
                                                    height: 6,
                                                    borderRadius: 3,
                                                    backgroundColor: alpha(theme.palette.divider, 0.2),
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        height: '100%',
                                                        width: `${member.availability}%`,
                                                        background: getAvailabilityGradient(member.availability),
                                                        borderRadius: 3,
                                                        transition: 'width 0.5s ease',
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* Top Skills */}
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {langStrings.employees.topSkills}
                                            </Typography>
                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                {member.topSkills.map((skill, skillIndex) => (
                                                    <Chip
                                                        key={skillIndex}
                                                        label={skill}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            bgcolor:
                                                                skillIndex === 0
                                                                    ? 'success.50'
                                                                    : skillIndex === 1
                                                                        ? 'info.50'
                                                                        : 'grey.100',
                                                            borderColor:
                                                                skillIndex === 0
                                                                    ? 'success.200'
                                                                    : skillIndex === 1
                                                                        ? 'info.200'
                                                                        : 'grey.300',
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        </Box>

                                        {/* Action Buttons */}
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<VisibilityIcon />}
                                                href={
                                                    member.id !== -1
                                                        ? `/${ELinks.PEOPLE_EMPLOYEES}/${member.id}`
                                                        : undefined
                                                }
                                                aria-label={`Profil von ${member.name} anzeigen`}
                                                sx={{ flex: 1, textTransform: 'none' }}
                                            >
                                                {langStrings.employees.viewProfile}
                                            </Button>
                                            {featureFlags.messages &&
                                                (member.id !== -1 ? (
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: 'primary.main' }}
                                                        href={`/${ELinks.MESSAGES}/compose?recipient=${member.id}`}
                                                        aria-label={`Nachricht an ${member.name} senden`}
                                                    >
                                                        <EmailIcon />
                                                    </IconButton>
                                                ) : (
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: 'primary.main' }}
                                                        aria-label={`Nachricht an ${member.name} senden`}
                                                        disabled
                                                    >
                                                        <EmailIcon />
                                                    </IconButton>
                                                ))}
                                            <IconButton
                                                size="small"
                                                sx={{ color: 'text.secondary' }}
                                                aria-label={`Weitere Optionen für ${member.name}`}
                                            >
                                                <Ellipsis size={16} />
                                            </IconButton>
                                        </Stack>
                                    </>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {/* View Full Team Button - Fixed at bottom */}
                <Button fullWidth variant="outlined" onClick={handleViewTeam} sx={{ mt: 2 }}>
                    {langStrings.employees.viewFullTeam}
                </Button>
            </CardContent>
        </BaseWidget>
    );
}
